import { ImpactLanguage, ModelEntityType } from '@upright/common/model/input/types';
import { useCompanyVariant } from '@upright/ui/api/model/input/company-variants';
import { BouLink } from '@upright/ui/common/generic/content/texts/bou-link';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useViewSetting } from '@upright/ui/common/generic/hooks';
import { leena2VersionMarkerSuffix } from '@upright/ui/common/upright/model/output/leena2-version-marker';
import { getOfficialTime } from '@upright/ui/untidy/redux/selectors';
import { useDefaultCompanyResult } from '@upright/ui/views/model-entity-views/company-view/context/use-default-company-result';
import { format } from 'date-fns';
import { default as finnishLocale, default as swedishLocale } from 'date-fns/locale/fi';
import { startCase } from 'lodash';
import { CSSProperties } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

interface Props {
  release: string | null;
  version: string;
  className?: string;
  language?: ImpactLanguage;
  style?: CSSProperties;
  showTimeZone?: boolean;
  // If provided, will try to render a link to the variant.
  variantLinkProps?: {
    type: ModelEntityType | undefined;
    variantId: string | undefined;
    companyId: string | undefined;
  };
}

export const ModelReleaseText = (props: Props) => {
  const time = useSelector(getOfficialTime);
  const timeString = getLocalizedTimeString(time, !!props.showTimeZone, props.language);

  const { type, variantId, companyId } = props.variantLinkProps ?? {};
  const isExpertMode = useViewSetting('companyProduct.modelReleaseVariantLink');
  const shouldRenderVariantLink = isExpertMode && type === ModelEntityType.COMPANY && !!variantId && !!companyId;

  return (
    <Root className={props.className} style={{ ...props.style }}>
      <div>{getLocalizedVersionString(props.release, props.version, props.language)}</div>
      <div>{timeString}</div>
      {shouldRenderVariantLink && (
        <div>
          <CompanyVariantLink variantId={variantId} companyId={companyId} />
        </div>
      )}
    </Root>
  );
};

const Root = styled.div`
  font-size: 11px;
  color: ${BouColor.GREY_B3B3B3};
  b {
    font-weight: 600;
  }
`;

function getLocalizedVersionString(
  release: string | null,
  version: string,
  language: ImpactLanguage = ImpactLanguage.en,
): JSX.Element {
  if (release === null) {
    switch (language) {
      case ImpactLanguage.en:
        return (
          <>
            Upright model version <b>{version}</b>
            {leena2VersionMarkerSuffix(version)}
          </>
        );
      case ImpactLanguage.fi:
        return (
          <>
            Upright-malliversio <b>{version}</b>
            {leena2VersionMarkerSuffix(version)}
          </>
        );
      case ImpactLanguage.sv:
        return (
          <>
            Upright modellversion <b>{version}</b>
            {leena2VersionMarkerSuffix(version)}
          </>
        );
    }
  }
  switch (language) {
    case ImpactLanguage.en:
      return (
        <>
          Upright model release <b>{release}</b>
        </>
      );
    case ImpactLanguage.fi:
      return (
        <>
          Upright-mallijulkaisu <b>{release}</b>
        </>
      );
    case ImpactLanguage.sv:
      return (
        <>
          Upright modellversion <b>{release}</b>
        </>
      );
  }
}

function getLocalizedTimeString(
  time: Date,
  showTimeZone: boolean,
  language: ImpactLanguage = ImpactLanguage.en,
): string {
  const timeFormat = `HH:mm${showTimeZone ? ' zzz' : ''}`;
  switch (language) {
    case ImpactLanguage.en:
      return `on ${format(time, 'do MMM. yyyy')} at ${simplifyGMT(format(time, timeFormat))}`;
    case ImpactLanguage.fi:
      return `${format(time, 'd.M.yyyy', { locale: finnishLocale })} klo ${simplifyGMT(
        format(time, timeFormat, { locale: finnishLocale }),
      )}`;
    case ImpactLanguage.sv:
      return `${format(time, 'd.M.yyyy', { locale: swedishLocale })} klo ${simplifyGMT(
        format(time, timeFormat, { locale: swedishLocale }),
      )}`;
  }
}
function simplifyGMT(timeString: string) {
  return timeString.replace('GMT+0', 'GMT');
}

function CompanyVariantLink(props: { variantId: string; companyId: string }) {
  const { variantId, companyId } = props;
  return (
    <BouLink to={`/internal/company-variants/${variantId}`}>
      <CompanyVariantLabel variantId={variantId} companyId={companyId} />
    </BouLink>
  );
}

function CompanyVariantLabel(props: { variantId: string; companyId: string }) {
  const result = useDefaultCompanyResult(props.companyId);
  const { data: variant } = useCompanyVariant(props.variantId);

  if (!result) {
    return <span>Variant id {props.variantId}</span>;
  }
  if (variant?.id !== (result.resultId as unknown as string)) {
    return <span>Warning: variant result mismatch</span>;
  }

  const { scenario } = variant.variantKey;
  const startDate = result.startDate || null;
  const endDate = result.endDate || null;

  return (
    <span>
      {startCase(scenario.label)} {scenario.nature} scenario
      <VariantDate {...{ startDate, endDate }} />
    </span>
  );
}

function VariantDate(props: { startDate: string | null; endDate: string | null }) {
  const { startDate, endDate } = props;
  if (!startDate || !endDate) {
    return <span />;
  }
  return <span> ({solveYear(startDate, endDate)})</span>;
}

function solveYear(startDate: string, endDate: string) {
  const [l, r] = [new Date(startDate), new Date(endDate)];
  const middle = new Date((l.getTime() + r.getTime()) / 2);
  return middle.getFullYear();
}
